import { EnumScreenNames } from "../../../enums/EnumScreenNames";
import { NextPageContextEnhanced } from "../../../types/page";
import SeoWidget from "../SeoWidget";
import { labelHelper, urlHelper } from "helpers/themeHelpers";
import { ISeoFaq, ITypeBreadcrumb } from "../types/types";
import { ICountryAsset } from "models/Assets/ICountryAsset";
import { ICategoryAsset } from "models/Assets/ICategoryAsset";

type SERPIndexingPayload = {
  postsCount: number;
  isFindPage: boolean;
  isWithCity: boolean;
  CPCount: number;
  serpType :string;
  subCategory: ICategoryAsset
};
type RealEstateIndexingPayload = { isNeighborhoodSelected: boolean };
type PostviewIndexingPayload = { categoryReportingName: string };
type TagDetailsIndexingPayload = { tagLang: string; currentLang };

type CustomIndexingPages =
  | EnumScreenNames.SERP_SCREEN
  | EnumScreenNames.REAL_ESTATE_PRICES_SCREEN
  | EnumScreenNames.POST_VIEW_SCREEN
  | EnumScreenNames.TAGS_DETAILS;

type IndexingStrategyPayload =
  | SERPIndexingPayload
  | PostviewIndexingPayload
  | RealEstateIndexingPayload
  | TagDetailsIndexingPayload;

export const initSeoData = async (
  context: NextPageContextEnhanced,
  screenKey: EnumScreenNames,
  payload: any
) => {
  const country = context.isChangeCountryPage
    ? context.essentialData
        .getCountries_ServerOnly()
        .filter(
          (country) => country.abbreviation == context.query?.ref?.split("_")[2]
        )?.[0]
    : context.countryObject;
  const seoWidget = SeoWidget.getInstance(
    process.env.COUNTRY_CODES,
    country,
    context.lang,
    context.essentialData,
    // @ts-ignore
    context.req.seoCache,
    context.hostNoSubDomain,
    context.host,
    (country?.abbreviation || "www") + "." + context.hostNoSubDomain
  );

  context.seoPage = await seoWidget.getSeoData(
    screenKey,
    payload,
    context.requestUri
  );
};

export function breadcrumbGenerate(
  crumbs: any[],
  lang: string
): ITypeBreadcrumb[] {
  let otherLang: string = lang == "ar" ? "en" : "ar";
  let breadcrumbs: ITypeBreadcrumb[] = [];
  let url = "/" + lang;
  let url2 = "/" + otherLang;

  crumbs.filter(Boolean).forEach((crumb) => {
    let label = labelHelper(crumb, lang);
    url = url.concat("/").concat(urlHelper(crumb, lang));
    url2 = url2.concat("/").concat(urlHelper(crumb, otherLang));
    breadcrumbs.push({
      label,
      url,
      url2,
    });
  });

  return breadcrumbs;
}

export const generateBreadcrumbs = <T extends object>(
  breadcrumbsArr: T[],
  lang: string,
  configs?: {
    extractLabelCb?: (val: any, lang: string) => string;
    extractUrlCb?: (val: any, lang: string) => string;
  }
) => {
  const labelHelperIntrenal = configs?.extractLabelCb || labelHelper;
  const urlHelperIntrenal = configs?.extractUrlCb || urlHelper;
  const breadcrumbsArrCopy = breadcrumbsArr.filter(
    (item) => labelHelperIntrenal(item, lang) || urlHelperIntrenal(item, lang)
  );
  const breadcrumbs = [];
  const prefix = ["en", "ar"].includes(
    urlHelperIntrenal(breadcrumbsArr[0], lang)
  )
    ? "/"
    : `/${lang}/`;
  breadcrumbsArrCopy.forEach((value, index) => {
    if (
      typeof value !== "object" ||
      value == null ||
      !Object.keys(value).length
    )
      return;
    let breadcrumbItem: { label: string; url?: string } = {
      label: labelHelperIntrenal(value, lang),
    };
    if (urlHelperIntrenal(value, lang)) {
      breadcrumbItem = {
        ...breadcrumbItem,
        url:
          prefix +
          breadcrumbsArrCopy
            .slice(0, index + 1)
            .map((val) => urlHelperIntrenal(val, lang))
            .join("/"),
      };
    }
    breadcrumbs.push(breadcrumbItem);
  });
  return breadcrumbs;
};

export const generateBreadcrumbsSchemaOrg = (
  breadcrumbsArr: ITypeBreadcrumb[],
  asc: boolean = false
): string => {
  let hostname = "https://__country_abb__.__host_name__";
  const breadcrumbsSchema = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbsArr.map((item, index) => {
      return {
        "@type": "ListItem",
        position: asc ? index + 1 : breadcrumbsArr.length - index,
        name: item.label,
        item: hostname + item.url,
      };
    }),
  };
  return JSON.stringify(breadcrumbsSchema);
};

export const generateHrefLangs = <T extends object>(
  urls: {
    urlAr: string;
    urlEn: string;
  },
  lang: string,
  country: ICountryAsset,
  hostName: string
) => {
  let hrefLangs = [];
  const arHrefLang = {
    href: `https://${country.abbreviation}.${hostName}/ar/${urls.urlAr}`,
    hrefLang: `ar-${country.abbreviation}`,
  };
  const enHrefLang = {
    href: `https://${country.abbreviation}.${hostName}/en/${urls.urlEn}`,
    hrefLang: `en-${country.abbreviation}`,
  };
  if (lang == "ar") {
    hrefLangs[0] = arHrefLang;
    hrefLangs[1] = enHrefLang;
  } else {
    hrefLangs[0] = enHrefLang;
    hrefLangs[1] = arHrefLang;
  }
  return hrefLangs;
};
export const generateFAQSchemaOrg = (FAQArray: ISeoFaq[]): string => {
  if (FAQArray.length == 0) return;
  const FAQSchema = {
    "@context": "https://schema.org/",
    "@type": "FAQPage",
    mainEntity: FAQArray.map((item) => {
      return {
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      };
    }),
  };
  return JSON.stringify(FAQSchema);
};

export const getCustomIndexStrategy: (
  screenName: EnumScreenNames,
  payload?: IndexingStrategyPayload | never,
  currentLang?: string
) => string = (screenName, payload, currentLang) => {
  const customIndexPages = [
    EnumScreenNames.POST_VIEW_SCREEN,
    EnumScreenNames.SERP_SCREEN,
    EnumScreenNames.REAL_ESTATE_PRICES_SCREEN,
    EnumScreenNames.TAGS_DETAILS,
  ];
  const calculateCustomIndexStrategy: {
    [key in CustomIndexingPages]: (
      payload: IndexingStrategyPayload,
      currentLang: string
    ) => string;
  } = {
    SERP_SCREEN: (payload: SERPIndexingPayload) => {
      const { postsCount, isFindPage, isWithCity, CPCount,serpType,subCategory } = payload;
      if (subCategory && subCategory.reporting_name == "JobSeeker") return "noindex,follow";
      if (serpType=="similar") return "noindex,nofollow";
      if (isFindPage) return "noindex,follow";
      if (postsCount == 0 && CPCount >= 1 && isWithCity)
        return "noindex,nofollow";
      return "index,follow";
    },
    POST_VIEW_SCREEN: (
      payload: PostviewIndexingPayload,
      currentLang: string
    ) => {
      const noIndexCategories = [
        "RealEstateForSale",
        "Autos",
        "RealEstateForRent",
        "MobileTablet",
        "JobSeeker"
      ];
      const { categoryReportingName } = payload;
      if (
        noIndexCategories.includes(categoryReportingName) ||
        currentLang == "en"
      )
        return "noindex,follow";
      return "index,follow";
    },
    REAL_ESTATE_PRICES_SCREEN: (payload: RealEstateIndexingPayload) => {
      if (payload.isNeighborhoodSelected) return "noindex,follow";
      return "index,follow";
    },
    TAGS_DETAILS: (payload: TagDetailsIndexingPayload, currentLang: string) => {
      if (payload.tagLang !== payload.currentLang) return "noindex,nofollow";
      return "index,follow";
    },
  };

  const noIndexNoFollowPages = [
    EnumScreenNames.CAR_REPORTS_SCREEN,
    EnumScreenNames.WEATHER_SCREEN,
    EnumScreenNames.PRAYER_TIME_SCREEN,
    EnumScreenNames.LOAN_CALCULATOR_SCREEN,
    EnumScreenNames.BUY_VS_RENT_SCREEN,
    EnumScreenNames.OTHER_COUNTRIES_SCREEN,
    EnumScreenNames.TERMS_OF_USE_SCREEN,
    EnumScreenNames.PRIVACY_POLICY_SCREEN,
    EnumScreenNames.ABOUT_US_SCREEN,
    EnumScreenNames.ADVERTISING_PRODUCTS,
    EnumScreenNames.SALES_REPRESENTATIVES,
    EnumScreenNames.BANNER_ADVERTISING,
    EnumScreenNames.OPEN_SHOP,
    EnumScreenNames.SAFETY_TIPS_SCREEN,
    EnumScreenNames.ADD_PICTURES_TIPS_SCREEN,
    EnumScreenNames.SUPPORT_INDEX_SCREEN,
    EnumScreenNames.ADVERTISING_PRODUCTS_PREMIUM,
    EnumScreenNames.ADVERTISING_PRODUCTS_BUMPUP,
    EnumScreenNames.ADVERTISING_PRODUCTS_TURBO,
    EnumScreenNames.ADVERTISING_PRODUCTS_ELAS,
    EnumScreenNames.ADVERTISING_PRODUCTS_VIP,
    EnumScreenNames.ADVERTISING_PRODUCTS_WALLET,
    EnumScreenNames.CONTACT_US_SCREEN,
    EnumScreenNames.SELLER_PAGES_SCREEN,
    EnumScreenNames.SEARCH_FOR_USER_NAME
  ];

  const noIndexFollowPages = [EnumScreenNames.MID_SCREEN];

  if (noIndexNoFollowPages.includes(screenName)) return "noindex,nofollow";
  if (noIndexFollowPages.includes(screenName)) return "noindex,follow";

  if (customIndexPages.includes(screenName)) {
    return (
      calculateCustomIndexStrategy[screenName] &&
      calculateCustomIndexStrategy[screenName](payload, currentLang)
    );
  }

  return "index,follow";
};
